<template>
    <!-- Off-canvas menu -->
    <div
        class="offcanvas offcanvas-end"
        tabindex="-1"
        id="compendiumMenu"
        aria-labelledby="compendiumMenuLabel"
        data-bs-backdrop="static"
    >
        <div class="offcanvas-header">
            <h4 class="offcanvas-title" id="compendiumMenuLabel">Menu</h4>
            <button
                type="button"
                class="btn-close text-reset"
                data-bs-dismiss="offcanvas"
                aria-label="Close"
            ></button>
        </div>

        <div class="offcanvas-body">
            <div class="d-grid gap-2 mb-3">
                <button
                    class="btn btn-primary text-center"
                    data-bs-toggle="modal"
                    data-bs-target="#cheatSheetModal"
                >
                    <font-awesome-icon icon="book-open" /> Cheat Sheet
                </button>
            </div>

            <hr />

            <!-- Import / Export -->
            <div class="d-grid gap-2 mb-3">
                <input
                    type="file"
                    accept="*/*"
                    ref="fileInput"
                    class="d-none"
                    @change="handleImport"
                />
                <button
                    class="btn btn-primary text-center"
                    @click="triggerImport"
                >
                    <font-awesome-icon icon="file-import" /> Import/Replace
                    Compendium
                </button>

                <input
                    type="file"
                    accept="*/*"
                    ref="fileAddInput"
                    class="d-none"
                    @change="handleAddImport"
                    :disabled="!hasCompendium"
                />
                <button
                    class="btn btn-primary text-center"
                    @click="triggerAddImport"
                    :disabled="!hasCompendium"
                >
                    <font-awesome-icon icon="file-import" /> Add Compendium
                </button>

                <button
                    class="btn btn-primary text-center"
                    @click="exportCompendium"
                    :disabled="!hasCompendium"
                >
                    <font-awesome-icon icon="save" /> Export Compendium
                </button>
            </div>

            <hr />

            <!-- Characters -->
            <div class="d-grid gap-2 mb-3">
                <input
                    type="file"
                    accept="*/*"
                    ref="characterFileInput"
                    class="d-none"
                    @change="handleCharacterImport"
                />
                <button
                    class="btn btn-primary text-center"
                    @click="triggerCharacterImport"
                >
                    <font-awesome-icon icon="file-import" /> Import Character
                </button>

                <input
                    type="file"
                    accept="*/*"
                    ref="storyFileInput"
                    class="d-none"
                    @change="handleStoryImport"
                />
                <button
                    class="btn btn-primary text-center"
                    @click="triggerStoryImport"
                >
                    <font-awesome-icon icon="file-import" /> Import Story
                </button>
            </div>

            <hr />

            <!-- Theme -->
            <div class="mb-3">
                <label class="form-label fw-bold h4">Theme</label>
                <div class="btn-group w-100" role="group">
                    <input
                        type="radio"
                        class="btn-check"
                        id="themeCore"
                        autocomplete="off"
                        :checked="store.theme === 1"
                        @change="setTheme(1)"
                    />
                    <label class="btn btn-outline-primary" for="themeCore"
                        >Core</label
                    >

                    <input
                        type="radio"
                        class="btn-check"
                        id="themeGothic"
                        autocomplete="off"
                        :checked="store.theme === 2"
                        @change="setTheme(2)"
                    />
                    <label class="btn btn-outline-primary" for="themeGothic"
                        >Gothic</label
                    >
                </div>
            </div>

            <hr />

            <!-- Add/Edit buttons -->
            <h4>Edit / Add</h4>

            <div class="btn-group w-100 mb-3">
                <button
                    class="btn btn-primary text-center"
                    data-bs-toggle="modal"
                    data-bs-target="#weaponCompendiumModal"
                    @click="modalMode = 'edit'"
                    :disabled="!hasCompendium"
                >
                    <font-awesome-icon icon="edit" /> Edit Weapon
                </button>
                <button
                    class="btn btn-secondary text-center"
                    data-bs-toggle="modal"
                    data-bs-target="#weaponCompendiumModal"
                    @click="modalMode = 'add'"
                    :disabled="!hasCompendium"
                >
                    <font-awesome-icon icon="plus" /> Add Weapon
                </button>
            </div>

            <div class="btn-group w-100 mb-3">
                <button
                    class="btn btn-primary text-center"
                    data-bs-toggle="modal"
                    data-bs-target="#weaponAbilityCompendiumModal"
                    @click="modalMode = 'edit'"
                    :disabled="!hasCompendium"
                >
                    <font-awesome-icon icon="edit" /> Edit Weapon Ability
                </button>
                <button
                    class="btn btn-secondary text-center"
                    data-bs-toggle="modal"
                    data-bs-target="#weaponAbilityCompendiumModal"
                    @click="modalMode = 'add'"
                    :disabled="!hasCompendium"
                >
                    <font-awesome-icon icon="plus" /> Add Weapon Ability
                </button>
            </div>

            <div class="btn-group w-100 mb-3">
                <button
                    class="btn btn-primary text-center"
                    data-bs-toggle="modal"
                    data-bs-target="#armourCompendiumModal"
                    @click="modalMode = 'edit'"
                    :disabled="!hasCompendium"
                >
                    <font-awesome-icon icon="edit" /> Edit Armour
                </button>
                <button
                    class="btn btn-secondary text-center"
                    data-bs-toggle="modal"
                    data-bs-target="#armourCompendiumModal"
                    @click="modalMode = 'add'"
                    :disabled="!hasCompendium"
                >
                    <font-awesome-icon icon="plus" /> Add Armour
                </button>
            </div>

            <div class="btn-group w-100 mb-3">
                <button
                    class="btn btn-primary text-center"
                    data-bs-toggle="modal"
                    data-bs-target="#armourAbilityCompendiumModal"
                    @click="modalMode = 'edit'"
                    :disabled="!hasCompendium"
                >
                    <font-awesome-icon icon="edit" /> Edit Armour Ability
                </button>
                <button
                    class="btn btn-secondary text-center"
                    data-bs-toggle="modal"
                    data-bs-target="#armourAbilityCompendiumModal"
                    @click="modalMode = 'add'"
                    :disabled="!hasCompendium"
                >
                    <font-awesome-icon icon="plus" /> Add Armour Ability
                </button>
            </div>

            <div class="btn-group w-100 mb-3">
                <button
                    class="btn btn-primary text-center"
                    data-bs-toggle="modal"
                    data-bs-target="#itemCompendiumModal"
                    @click="modalMode = 'edit'"
                    :disabled="!hasCompendium"
                >
                    <font-awesome-icon icon="edit" /> Edit Item
                </button>
                <button
                    class="btn btn-secondary text-center"
                    data-bs-toggle="modal"
                    data-bs-target="#itemCompendiumModal"
                    @click="modalMode = 'add'"
                    :disabled="!hasCompendium"
                >
                    <font-awesome-icon icon="plus" /> Add Item
                </button>
            </div>

            <div class="btn-group w-100 mb-3">
                <button
                    class="btn btn-primary text-center"
                    data-bs-toggle="modal"
                    data-bs-target="#itemAbilityCompendiumModal"
                    @click="modalMode = 'edit'"
                    :disabled="!hasCompendium"
                >
                    <font-awesome-icon icon="edit" /> Edit Item Ability
                </button>
                <button
                    class="btn btn-secondary text-center"
                    data-bs-toggle="modal"
                    data-bs-target="#itemAbilityCompendiumModal"
                    @click="modalMode = 'add'"
                    :disabled="!hasCompendium"
                >
                    <font-awesome-icon icon="plus" /> Add Item Ability
                </button>
            </div>

            <div class="btn-group w-100 mb-3">
                <button
                    class="btn btn-primary text-center"
                    data-bs-toggle="modal"
                    data-bs-target="#monsterTypeCompendiumModal"
                    @click="modalMode = 'edit'"
                    :disabled="!hasCompendium"
                >
                    <font-awesome-icon icon="edit" /> Edit Creature Type
                    Reactions
                </button>
                <button
                    class="btn btn-secondary text-center"
                    data-bs-toggle="modal"
                    data-bs-target="#monsterTypeCompendiumModal"
                    @click="modalMode = 'add'"
                    :disabled="!hasCompendium"
                >
                    <font-awesome-icon icon="plus" /> Add Creature Type
                    Reactions
                </button>
            </div>

            <div class="btn-group w-100 mb-3">
                <button
                    class="btn btn-primary text-center"
                    data-bs-toggle="modal"
                    data-bs-target="#bestiaryCompendiumModal"
                    @click="modalMode = 'edit'"
                    :disabled="!hasCompendium"
                >
                    <font-awesome-icon icon="edit" /> Edit Creature
                </button>
                <button
                    class="btn btn-secondary text-center"
                    data-bs-toggle="modal"
                    data-bs-target="#bestiaryCompendiumModal"
                    @click="modalMode = 'add'"
                    :disabled="!hasCompendium"
                >
                    <font-awesome-icon icon="plus" /> Add Creature
                </button>
            </div>

            <div class="btn-group w-100 mb-3">
                <button
                    class="btn btn-primary text-center"
                    data-bs-toggle="modal"
                    data-bs-target="#bestiaryAttackCompendiumModal"
                    @click="modalMode = 'edit'"
                    :disabled="!hasCompendium"
                >
                    <font-awesome-icon icon="edit" /> Edit Bestiary Attack
                </button>
                <button
                    class="btn btn-secondary text-center"
                    data-bs-toggle="modal"
                    data-bs-target="#bestiaryAttackCompendiumModal"
                    @click="modalMode = 'add'"
                    :disabled="!hasCompendium"
                >
                    <font-awesome-icon icon="plus" /> Add Bestiary Attack
                </button>
            </div>

            <div class="btn-group w-100 mb-3">
                <button
                    class="btn btn-primary text-center"
                    data-bs-toggle="modal"
                    data-bs-target="#bestiaryAbilityCompendiumModal"
                    @click="modalMode = 'edit'"
                    :disabled="!hasCompendium"
                >
                    <font-awesome-icon icon="edit" /> Edit Bestiary Ability
                </button>
                <button
                    class="btn btn-secondary text-center"
                    data-bs-toggle="modal"
                    data-bs-target="#bestiaryAbilityCompendiumModal"
                    @click="modalMode = 'add'"
                    :disabled="!hasCompendium"
                >
                    <font-awesome-icon icon="plus" /> Add Bestiary Ability
                </button>
            </div>
        </div>
    </div>

    <!-- Modals -->
    <weapon-compendium-modal :mode="modalMode" />
    <weapon-ability-compendium-modal :mode="modalMode" />
    <armour-compendium-modal :mode="modalMode" />
    <armour-ability-compendium-modal :mode="modalMode" />
    <item-compendium-modal :mode="modalMode" />
    <item-ability-compendium-modal :mode="modalMode" />
    <bestiary-compendium-modal :mode="modalMode" />
    <bestiary-attack-compendium-modal :mode="modalMode" />
    <bestiary-ability-compendium-modal :mode="modalMode" />
    <monster-type-compendium-modal :mode="modalMode" />
    <cheat-sheet-modal :theme="store.theme"/>
</template>

<script>
import { useRosterStore } from "@/stores/index";
import WeaponCompendiumModal from "@/components/WeaponCompendiumModal.vue";
import WeaponAbilityCompendiumModal from "@/components/WeaponAbilityCompendiumModal.vue";
import ArmourCompendiumModal from "@/components/ArmourCompendiumModal.vue";
import ArmourAbilityCompendiumModal from "@/components/ArmourAbilityCompendiumModal.vue";
import ItemCompendiumModal from "./ItemCompendiumModal.vue";
import ItemAbilityCompendiumModal from "./ItemAbilityCompendiumModal.vue";
import BestiaryCompendiumModal from "./BestiaryCompendiumModal.vue";
import BestiaryAttackCompendiumModal from "./BestiaryAttackCompendiumModal.vue";
import BestiaryAbilityCompendiumModal from "./BestiaryAbilityCompendiumModal.vue";
import MonsterTypeCompendiumModal from "./MonsterTypeCompendiumModal.vue";
import CheatSheetModal from "@/components/CheatSheetModal.vue";
import { Offcanvas } from "bootstrap";

export default {
    name: "CompendiumMenu",
    components: {
        WeaponCompendiumModal,
        WeaponAbilityCompendiumModal,
        ArmourCompendiumModal,
        ArmourAbilityCompendiumModal,
        ItemCompendiumModal,
        ItemAbilityCompendiumModal,
        BestiaryCompendiumModal,
        BestiaryAttackCompendiumModal,
        BestiaryAbilityCompendiumModal,
        MonsterTypeCompendiumModal,
        CheatSheetModal,
    },
    data() {
        return {
            store: useRosterStore(),
            modalMode: "add", // "add" | "edit"
        };
    },
    props: {
        page: { type: String, required: true }, // "roster" | "encounter-manager"
    },
    computed: {
        hasCompendium() {
            return (
                this.store.weapons.length > 0 ||
                this.store.weaponAbilities.length > 0 ||
                this.store.armour.length > 0 ||
                this.store.armourAbilities.length > 0
            );
        },
    },
    methods: {
        triggerImport() {
            this.$refs.fileInput.click();
        },
        triggerAddImport() {
            this.$refs.fileAddInput.click();
        },
        async handleAddImport(event) {
            const file = event.target.files[0];
            if (!file) return;

            if (!file.name.endsWith(".compendium")) {
                alert("Please select a .compendium file.");
                event.target.value = "";
                return;
            }

            try {
                const text = await file.text();
                const compendium = JSON.parse(text);
                this.store.appendToCompendium(compendium);
                alert("Compendium data added successfully!");
            } catch (err) {
                console.error("Failed to add to compendium:", err);
                alert("Invalid compendium file.");
            }
            event.target.value = "";
        },
        async handleImport(event) {
            const file = event.target.files[0];
            if (!file) return;

            if (!file.name.endsWith(".compendium")) {
                alert("Please select a .compendium file.");
                event.target.value = "";
                return;
            }

            try {
                const text = await file.text();
                const compendium = JSON.parse(text);
                if (
                    confirm(
                        "Do you want to overwrite existing compendium data? This action cannot be undone."
                    )
                ) {
                    this.store.overwriteCompendium(compendium);
                } else {
                    return;
                }
                alert("Compendium imported successfully!");
            } catch (err) {
                console.error("Failed to import compendium:", err);
                alert("Invalid compendium file.");
            }
            event.target.value = "";
        },
        exportCompendium() {
            const dataStr = this.store.exportCompendium();
            const blob = new Blob([dataStr], {
                type: "application/octet-stream",
            }); // ✅ Safari-safe
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = "compendium.compendium"; // ✅ no .json
            a.click();
            URL.revokeObjectURL(url);
        },
        triggerCharacterImport() {
            this.$refs.characterFileInput.click();
        },
        triggerStoryImport() {
            this.$refs.storyFileInput.click();
        },
        goto(target) {
            const offcanvasEl = document.getElementById("compendiumMenu");
            if (offcanvasEl) {
                let bsOffcanvas = Offcanvas.getInstance(offcanvasEl);
                if (!bsOffcanvas) {
                    bsOffcanvas = new Offcanvas(offcanvasEl);
                }
                bsOffcanvas.hide();
            }

            // Cleanup + navigate
            setTimeout(() => {
                document
                    .querySelectorAll(".offcanvas-backdrop")
                    .forEach((el) => el.remove());

                this.$router.push({ name: target });
            }, 300);
        },
        async handleCharacterImport(event) {
            const file = event.target.files[0];
            if (!file) return;

            if (!file.name.endsWith(".character")) {
                alert("Please select a .character file.");
                event.target.value = "";
                return;
            }

            try {
                const text = await file.text();
                const character = JSON.parse(text);

                this.store.addCharacter(character);
                alert("Character imported successfully!");

                // Close offcanvas
                const offcanvasEl = document.getElementById("compendiumMenu");
                if (offcanvasEl) {
                    let bsOffcanvas = Offcanvas.getInstance(offcanvasEl);
                    if (!bsOffcanvas) {
                        bsOffcanvas = new Offcanvas(offcanvasEl);
                    }
                    bsOffcanvas.hide();
                }

                // Cleanup + navigate
                setTimeout(() => {
                    document
                        .querySelectorAll(".offcanvas-backdrop")
                        .forEach((el) => el.remove());

                    this.$router.push({
                        name: "Character",
                        params: { id: character.id },
                    });
                }, 300);
            } catch (err) {
                console.error("Failed to import character:", err);
                alert("Invalid character file.");
            }
            event.target.value = "";
        },
        async handleStoryImport(event) {
            const file = event.target.files[0];
            if (!file) return;

            if (!file.name.endsWith(".story")) {
                alert("Please select a .story file.");
                event.target.value = "";
                return;
            }

            try {
                const text = await file.text();
                const story = JSON.parse(text);

                this.store.addStory(story);
                alert("Story imported successfully!");

                // Close offcanvas
                const offcanvasEl = document.getElementById("compendiumMenu");
                if (offcanvasEl) {
                    let bsOffcanvas = Offcanvas.getInstance(offcanvasEl);
                    if (!bsOffcanvas) {
                        bsOffcanvas = new Offcanvas(offcanvasEl);
                    }
                    bsOffcanvas.hide();
                }

                // Cleanup + navigate
                setTimeout(() => {
                    document
                        .querySelectorAll(".offcanvas-backdrop")
                        .forEach((el) => el.remove());

                    this.$router.push({
                        name: "Story",
                        params: { id: story.id },
                    });
                }, 300);
            } catch (err) {
                console.error("Failed to import story:", err);
                alert("Invalid story file.");
            }
            event.target.value = "";
        },
        setTheme(themeNumber) {
            this.store.theme = themeNumber;
        },
    },
};
</script>

<style>
.offcanvas-body .btn {
    text-align: left;
}
</style>

